import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../../components/commons/Sidebar";
import Swal from "sweetalert2";
import { getUploadedFiles, deleteUploadedFile } from "../../api/uploadAPI";

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const MediaAdmin = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | used | unused
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null); // filename being previewed
  const [modalCopied, setModalCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUploadedFiles();
      setFiles(data);
    } catch {
      Swal.fire({ icon: "error", text: "Failed to load file list." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (file) => {
    if (file.inUse) {
      const { isConfirmed } = await Swal.fire({
        icon: "warning",
        title: "File sedang dipakai!",
        html: `File <b>${file.filename}</b> masih digunakan di suatu konten.<br/>Tetap hapus?`,
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        confirmButtonText: "Force Delete",
        cancelButtonText: "Cancel",
      });
      if (!isConfirmed) return;
      await deleteUploadedFile(file.filename, true);
    } else {
      const { isConfirmed } = await Swal.fire({
        icon: "warning",
        title: "Delete this file?",
        text: file.filename,
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
      });
      if (!isConfirmed) return;
      await deleteUploadedFile(file.filename, false);
    }
    await load();
    if (preview === file.filename) setPreview(null);
  };

  const filtered = files.filter((f) => {
    if (filter === "used" && !f.inUse) return false;
    if (filter === "unused" && f.inUse) return false;
    if (search && !f.filename.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalSize = files.reduce((s, f) => s + f.size, 0);
  const unusedCount = files.filter((f) => !f.inUse).length;
  const unusedSize = files.filter((f) => !f.inUse).reduce((s, f) => s + f.size, 0);

  const BASE = process.env.REACT_APP_EXPRESS_API_BASE_URL || "http://localhost:3001";

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-900">
      <Sidebar />
      <div className="flex-1 p-6 lg:p-10 overflow-auto">
        {/* Header */}
        <div className="mb-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-indigo-400">Management</p>
          <h2 className="text-3xl font-black text-white">MEDIA & IMAGES</h2>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total File" value={files.length} color="indigo" />
          <StatCard label="Total Size" value={formatSize(totalSize)} color="blue" />
          <StatCard label="Unused" value={unusedCount} color={unusedCount > 0 ? "amber" : "green"} />
          <StatCard label="Deletable" value={formatSize(unusedSize)} color={unusedSize > 0 ? "red" : "green"} />
        </div>

        {/* Filter & Search */}
        <div className="flex flex-wrap gap-3 mb-6 items-center">
          <div className="flex gap-1 bg-zinc-800/60 rounded-xl p-1">
            {[["all", "All"], ["used", "Used"], ["unused", "Unused"]].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setFilter(val)}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all ${
                  filter === val ? "bg-indigo-600 text-white" : "text-zinc-400 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Search filename..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-64"
          />
          <button onClick={load} className="ml-auto px-4 py-2 text-xs font-bold text-zinc-400 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded-xl transition-all">
            Refresh
          </button>
        </div>

        {loading ? (
          <p className="text-zinc-500 text-sm">Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-zinc-600">
            <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm font-semibold">No files found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filtered.map((file) => (
              <FileCard
                key={file.filename}
                file={file}
                base={BASE}
                onDelete={handleDelete}
                onPreview={() => setPreview(file.filename)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {preview && (() => {
        const file = files.find((f) => f.filename === preview);
        if (!file) return null;
        return (
          <div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setPreview(null)}
          >
            <div
              className="relative bg-zinc-900 rounded-2xl shadow-2xl border border-zinc-700 max-w-2xl w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-700">
                <div>
                  <p className="text-white font-bold text-sm truncate max-w-xs">{file.filename}</p>
                  <p className="text-zinc-500 text-xs">{formatSize(file.size)} · {formatDate(file.uploadedAt)}</p>
                </div>
                <button onClick={() => setPreview(null)} className="text-zinc-500 hover:text-white transition-colors p-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6 flex items-center justify-center bg-zinc-950/50 min-h-[300px]">
                <img
                  src={`${BASE}${file.url.startsWith("http") ? file.url.replace(/^https?:\/\/[^/]+/, "") : file.url}`}
                  alt={file.filename}
                  className="max-h-[60vh] max-w-full object-contain rounded-lg"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </div>
              <div className="px-6 py-4 border-t border-zinc-700 flex items-center justify-between gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                  file.inUse ? "bg-green-500/20 text-green-400" : "bg-amber-500/20 text-amber-400"
                }`}>
                  {file.inUse ? "In Use" : "Unused"}
                </span>
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    onClick={() => {
                      const url = file.url.startsWith("http") ? file.url : `${BASE}${file.url}`;
                      navigator.clipboard.writeText(url).then(() => {
                        setModalCopied(true);
                        setTimeout(() => setModalCopied(false), 2000);
                      });
                    }}
                    className={`px-4 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-2 ${modalCopied ? "bg-green-500/20 text-green-400" : "bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400"}`}
                  >
                    {modalCopied ? "Copied!" : "Copy Link"}
                  </button>
                  <button
                    onClick={() => handleDelete(file)}
                    className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold rounded-xl transition-all"
                  >
                    Delete File
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const StatCard = ({ label, value, color }) => {
  const colors = {
    indigo: "text-indigo-400 bg-indigo-500/10 border-indigo-500/20",
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    red: "text-red-400 bg-red-500/10 border-red-500/20",
    green: "text-green-400 bg-green-500/10 border-green-500/20",
  };
  return (
    <div className={`rounded-2xl border p-4 ${colors[color]}`}>
      <p className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-1">{label}</p>
      <p className="text-xl font-black">{value}</p>
    </div>
  );
};

const FileCard = ({ file, base, onDelete, onPreview }) => {
  const [copied, setCopied] = useState(false);
  const imgSrc = file.url.startsWith("http") ? file.url : `${base}${file.url}`;
  const fullUrl = file.url.startsWith("http") ? file.url : `${base}${file.url}`;

  const copyLink = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="group relative bg-zinc-800/50 border border-zinc-700/50 rounded-2xl overflow-hidden hover:border-indigo-500/40 transition-all">
      {/* Thumbnail */}
      <div
        className="aspect-square bg-zinc-900 cursor-pointer flex items-center justify-center overflow-hidden"
        onClick={onPreview}
      >
        <img
          src={imgSrc}
          alt={file.filename}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            e.target.style.display = "none";
            e.target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center text-zinc-600"><svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg></div>`;
          }}
        />
      </div>

      {/* Status badge */}
      <div className="absolute top-2 left-2">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm ${
          file.inUse ? "bg-green-500/80 text-white" : "bg-amber-500/80 text-white"
        }`}>
          {file.inUse ? "Used" : "Free"}
        </span>
      </div>

      {/* Action buttons */}
      <div className="absolute top-2 right-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={copyLink}
          className={`p-1.5 rounded-lg backdrop-blur-sm text-white transition-all ${copied ? "bg-green-500/90" : "bg-indigo-500/80 hover:bg-indigo-600"}`}
          title="Copy link"
        >
          {copied ? (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          )}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(file); }}
          className="p-1.5 bg-red-500/80 hover:bg-red-600 text-white rounded-lg backdrop-blur-sm"
          title="Delete"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="text-white text-xs font-semibold truncate" title={file.filename}>{file.filename}</p>
        <p className="text-zinc-500 text-[10px] mt-0.5">{formatSize(file.size)}</p>
        <p className="text-zinc-600 text-[10px]">{formatDate(file.uploadedAt)}</p>
      </div>
    </div>
  );
};

export default MediaAdmin;
